import { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CalendarX, CalendarCheck, Clock, CircleNotch, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PublicBookingManage() {
  const { slug, bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showResched, setShowResched] = useState(false);
  const [day, setDay] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); });
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");

  const load = () =>
    api.get(`/public/booking/${slug}/manage/${bookingId}`, { params: { token } })
      .then((r) => setData(r.data)).catch(() => setNotFound(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, bookingId, token]);

  useEffect(() => {
    setSlots(null); setSlot("");
    if (!showResched || !data?.booking?.service_id || !day) return;
    api.get(`/public/booking/${slug}/availability`, { params: { service_id: data.booking.service_id, day } })
      .then((r) => setSlots(r.data)).catch(() => setSlots({ slots: [] }));
  }, [showResched, day, slug, data]);

  const cancel = async () => {
    if (!window.confirm("Cancel this booking?")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/public/booking/${slug}/manage/${bookingId}/cancel`, { token });
      setResult(res.rowan_message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel."); }
    finally { setBusy(false); }
  };

  const reschedule = async () => {
    if (!slot) return toast.error("Pick a new time.");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/public/booking/${slug}/manage/${bookingId}/reschedule`, { token, date: day, start_time: slot });
      setResult(res.rowan_message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reschedule."); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F7F4" }}>
      <p className="text-base" style={label} data-testid="manage-notfound">We couldn't find that booking.</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F7F4" }}>
      <CircleNotch size={28} className="animate-spin" color="var(--bk-accent)" />
    </div>
  );

  if (result) return (
    <div className="min-h-screen px-4 py-16" style={{ background: "#F8F7F4" }}>
      <div className="max-w-xl mx-auto bk-card p-8 text-center" data-testid="manage-result">
        <CheckCircle size={44} weight="fill" color="var(--bk-success)" className="mx-auto" />
        <p className="text-base mt-4" style={{ color: "var(--bk-text)" }}>{result}</p>
        <a href={`/book/${slug}`} className="bk-btn-primary inline-block px-5 py-2.5 rounded-full text-sm font-semibold mt-6" data-testid="manage-book-again">Book a new session</a>
      </div>
    </div>
  );

  const b = data.booking;
  return (
    <div className="min-h-screen px-4 py-12" style={{ background: "#F8F7F4" }} data-testid="manage-page">
      <div className="max-w-xl mx-auto space-y-5">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>{data.business_name}</h1>
          <p className="text-sm mt-1" style={label}>Manage your booking</p>
        </div>

        <div className="bk-card p-6" data-testid="manage-booking-card">
          <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{b.label || b.equipment}</p>
          <p className="text-sm mt-1" style={label}>{b.date} · {b.start_time}–{b.end_time} · {b.equipment}</p>
          <p className="text-sm" style={label}>For {b.booked_by} · status: <span className="font-semibold" style={{ color: b.status === "Scheduled" ? "var(--bk-success)" : "var(--bk-text)" }}>{b.status}</span></p>
          {data.package && (
            <p className="text-xs mt-2 font-semibold" style={{ color: "var(--bk-accent)" }} data-testid="manage-package">
              Using {data.package.item_name} — {data.package.sessions_remaining} session{data.package.sessions_remaining !== 1 ? "s" : ""} remaining
            </p>
          )}
          <p className="text-sm mt-3 rounded-xl border px-4 py-3" style={{ borderColor: "rgba(232,168,56,0.5)", background: "#FFF9F0", color: "var(--bk-text)" }} data-testid="manage-policy-text">{data.policy_text}</p>
          {data.has_email && b.status === "Scheduled" && (
            <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" style={{ color: "var(--bk-text)" }}>
              <input type="checkbox" checked={data.reminder_opt_in} data-testid="manage-reminder-toggle"
                onChange={async (e) => {
                  try {
                    const { data: res } = await api.post(`/public/booking/${slug}/manage/${bookingId}/reminder`, { token, opt_in: e.target.checked });
                    toast.success(res.rowan_message);
                    load();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update the reminder."); }
                }} />
              Email me a reminder the day before
            </label>
          )}
        </div>

        {(data.can_cancel || data.can_reschedule) && (
          <div className="bk-card p-6 space-y-4">
            <div className="flex flex-wrap gap-2">
              {data.can_reschedule && (
                <button onClick={() => setShowResched((s) => !s)} data-testid="manage-reschedule-toggle"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
                  <CalendarCheck size={15} weight="bold" /> Reschedule
                </button>
              )}
              {data.can_cancel && (
                <button onClick={cancel} disabled={busy} data-testid="manage-cancel-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "#B23B1F" }}>
                  {busy ? <CircleNotch size={15} className="animate-spin" /> : <CalendarX size={15} weight="bold" />} Cancel booking
                </button>
              )}
            </div>
            {showResched && (
              <div className="space-y-3 pt-3 border-t" style={{ borderColor: "var(--bk-border)" }} data-testid="manage-reschedule-panel">
                <DateInput min={todayISO()} value={day} onChange={(e) => setDay(e.target.value)} data-testid="manage-resched-date"
                  className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                {slots === null ? (
                  <p className="text-sm" style={label}>Checking availability…</p>
                ) : (() => {
                  const future = slots.slots.filter((t) => new Date(`${day}T${t}:00`) > new Date());
                  return future.length === 0 ? (
                    <p className="text-sm" style={label} data-testid="manage-no-slots">{slots.note || "No open slots that day — try another date."}</p>
                  ) : (
                  <div className="flex flex-wrap gap-2" data-testid="manage-slots-container">
                    {future.map((t) => (
                      <button key={t} onClick={() => setSlot(t)} data-testid={`manage-slot-${t}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                        style={slot === t ? { background: "var(--bk-accent)", color: "#fff", borderColor: "var(--bk-accent)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                        <Clock size={13} weight="bold" /> {t}
                      </button>
                    ))}
                  </div>
                  );
                })()}
                {slot && (
                  <button onClick={reschedule} disabled={busy} data-testid="manage-resched-confirm"
                    className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                    {busy ? <CircleNotch size={15} className="animate-spin" /> : <ArrowRight size={15} weight="bold" />} Move to {day} at {slot}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
